/**
 * The receiving side of a Nearby offer as a state machine: `step(state, input)` returns the next state and the
 * effects to carry out, and does nothing else. Every rule that keeps a lying server or a double click from
 * getting a key lives in this one table (and `test/nearbyGuest.test.ts` walks it): the hook that runs it only
 * performs the effects and feeds their outcomes back as inputs.
 */
import type { GuestKey } from '@/crypto';
import type { BridgeMode, NearbyCloseReason, NearbyPeer } from '@/api/types';
import type { Sas } from '@/lib/nearby';

export type IncomingPhase = 'ask' | 'receiving' | 'closed' | 'failed' | 'mismatch';

/** What happens to a bridge offered to this device. */
export interface Incoming {
  readonly offerId: string;
  readonly from: NearbyPeer;
  readonly mode: BridgeMode;
  /** What the owner committed to with the offer. */
  readonly ownerKeyHash: string;
  /** The owner's key, once it has shown it and it matched the commitment. */
  readonly ownerPublicKey: string | null;
  readonly pair: GuestKey;
  /** Known once the owner's key is: until then the reels spin. */
  readonly emoji: readonly Sas[] | null;
  readonly phase: IncomingPhase;
  readonly reason?: NearbyCloseReason | undefined;
}

export interface OfferInput {
  readonly offerId: string;
  readonly from: NearbyPeer;
  readonly mode: BridgeMode;
  readonly ownerKeyHash: string;
}

export interface ReadyInput {
  readonly offerId: string;
  readonly bridgeId: string;
  readonly wrappedKey: string;
  readonly claimToken: string;
}

export type GuestInput =
  /** An offer arrived and a fresh key pair was made for it. */
  | { readonly type: 'offer'; readonly offer: OfferInput; readonly pair: GuestKey }
  /** An offer that `admitsOffer` turned away, before any key pair was made. */
  | { readonly type: 'refuse'; readonly offerId: string }
  /** The owner's key was checked against its commitment; `emoji` is null when it did not match. */
  | { readonly type: 'revealed'; readonly offerId: string; readonly ownerPublicKey: string; readonly emoji: readonly Sas[] | null }
  | { readonly type: 'accept' }
  | { readonly type: 'decline' }
  | { readonly type: 'closed'; readonly offerId: string; readonly reason: NearbyCloseReason }
  | { readonly type: 'ready'; readonly ready: ReadyInput }
  | { readonly type: 'unwrapped'; readonly offerId: string }
  | { readonly type: 'unwrapFailed'; readonly offerId: string }
  | { readonly type: 'answerFailed'; readonly offerId: string }
  | { readonly type: 'acceptFailed'; readonly offerId: string; readonly reason: NearbyCloseReason }
  /** The page got a new token: whatever was in progress was closed on the server with the old one. */
  | { readonly type: 'tokenChanged' };

export type GuestEffect =
  | { readonly type: 'answer'; readonly offerId: string; readonly guestPublicKey: string }
  | { readonly type: 'accept'; readonly offerId: string }
  | { readonly type: 'decline'; readonly offerId: string }
  | { readonly type: 'unwrap'; readonly incoming: Incoming; readonly ready: ReadyInput }
  /** The exchange ended without a key: its KEM secret is wiped. */
  | { readonly type: 'discard'; readonly pair: GuestKey };

export interface GuestStep {
  readonly state: Incoming | null;
  readonly effects: readonly GuestEffect[];
}

const COMMITMENT = /^[A-Za-z0-9_-]{43}$/;

const stay = (state: Incoming | null): GuestStep => ({ state, effects: [] });

const live = (state: Incoming | null): state is Incoming & { readonly phase: 'ask' | 'receiving' } => state !== null && (state.phase === 'ask' || state.phase === 'receiving');

/** Ends the exchange on `phase`, wiping the secret. */
function end(state: Incoming, phase: Exclude<IncomingPhase, 'ask' | 'receiving'>, reason?: NearbyCloseReason, ...effects: GuestEffect[]): GuestStep {
  return { state: { ...state, phase, reason }, effects: [...effects, { type: 'discard', pair: state.pair }] };
}

/**
 * Whether an owner's key shown for `offerId` is worth checking at all: only the first one, for the offer on screen,
 * before the person has pressed anything.
 */
export function expectsReveal(state: Incoming | null, offerId: string): state is Incoming {
  return state?.offerId === offerId && state.phase === 'ask' && state.ownerPublicKey === null;
}

/** Whether an offer may start an exchange: nothing else on screen, and a commitment of the shape this build sends. */
export function admitsOffer(state: Incoming | null, offer: OfferInput): boolean {
  return state === null && COMMITMENT.test(offer.ownerKeyHash);
}

export function step(state: Incoming | null, input: GuestInput): GuestStep {
  switch (input.type) {
    case 'offer': {
      const { offer, pair } = input;
      // While any dialog is on screen, even one that has ended, a new offer is declined: a server must not be able to
      // offer again and again, each time with a fresh key, until a pair of keys shows matching emoji, without the person
      // ever pressing anything. One answered offer is one try. An offer that is not ours (an older build) goes the same way.
      if (!admitsOffer(state, offer)) {
        return { state, effects: [{ type: 'decline', offerId: offer.offerId }, { type: 'discard', pair }] };
      }
      return {
        state: { offerId: offer.offerId, from: offer.from, mode: offer.mode, ownerKeyHash: offer.ownerKeyHash, ownerPublicKey: null, pair, emoji: null, phase: 'ask' },
        effects: [{ type: 'answer', offerId: offer.offerId, guestPublicKey: pair.publicKey }],
      };
    }
    case 'refuse':
      return { state, effects: [{ type: 'decline', offerId: input.offerId }] };
    case 'revealed': {
      if (!expectsReveal(state, input.offerId)) return stay(state);
      if (input.emoji === null) return end(state, 'mismatch', undefined, { type: 'decline', offerId: state.offerId });
      return { state: { ...state, ownerPublicKey: input.ownerPublicKey, emoji: input.emoji }, effects: [] };
    }
    case 'accept': {
      // only a person who has the emoji in front of them, once
      if (state?.phase !== 'ask' || state.emoji === null) return stay(state);
      return { state: { ...state, phase: 'receiving' }, effects: [{ type: 'accept', offerId: state.offerId }] };
    }
    case 'decline': {
      if (state === null) return stay(null);
      const effects: GuestEffect[] = live(state) ? [{ type: 'decline', offerId: state.offerId }] : [];
      return { state: null, effects: [...effects, { type: 'discard', pair: state.pair }] };
    }
    case 'closed': {
      if (state?.offerId !== input.offerId || state.phase === 'failed' || state.phase === 'mismatch') return stay(state);
      return end(state, 'closed', input.reason);
    }
    case 'ready': {
      const { ready } = input;
      if (state?.offerId !== ready.offerId) return stay(state);
      // a key arrives only for a person who compared the emoji and pressed Accept; anything else is not honoured
      if (state.phase !== 'receiving' || state.ownerPublicKey === null) return end(state, 'failed', undefined, { type: 'decline', offerId: state.offerId });
      return { state, effects: [{ type: 'unwrap', incoming: state, ready }] };
    }
    case 'unwrapped':
      return state?.offerId === input.offerId ? { state: null, effects: [{ type: 'discard', pair: state.pair }] } : stay(state);
    case 'unwrapFailed':
      return state?.offerId === input.offerId ? end(state, 'failed') : stay(state);
    case 'answerFailed':
      return state?.offerId === input.offerId && live(state) ? end(state, 'closed', 'expired') : stay(state);
    case 'acceptFailed':
      return state?.offerId === input.offerId && state.phase === 'receiving' ? end(state, 'closed', input.reason) : stay(state);
    case 'tokenChanged':
      return live(state) ? end(state, 'closed', 'gone') : stay(state);
  }
}
